// viewer.zoom.js — pure zoom + pan controller for the <video> element.
//
// No DOM, no globals. The viewer wires DOM events to these methods and
// reads back { zoom, tx, ty } to write a CSS transform.
//
// Coordinate convention: cursorX/cursorY and drag x/y are in the same CSS
// pixel space as viewport.width/viewport.height (panel-local). Pan tx/ty
// are translations in those CSS pixels and are applied AFTER scale, i.e.
// `transform: translate(tx, ty) scale(zoom)`. The transform-origin is the
// panel centre.
//
// See docs/superpowers/specs/2026-05-11-smooth-scrub-phase-a-design.md §6A.

const DEFAULTS = {
  minZoom: 1.0,
  // 8x since 2026-09-18: the 4K rungs and the native H.265 copy carry the
  // detail for it (4x was set when 1080p was the top of the ladder).
  maxZoom: 8.0,
  wheelStepPer100: 1.1, // 10% per 100 deltaY units
};

export function createZoomController(opts = {}) {
  const minZoom = Number.isFinite(opts.minZoom) ? opts.minZoom : DEFAULTS.minZoom;
  const maxZoom = Number.isFinite(opts.maxZoom) ? opts.maxZoom : DEFAULTS.maxZoom;
  const wheelStepPer100 = Number.isFinite(opts.wheelStepPer100)
    ? opts.wheelStepPer100
    : DEFAULTS.wheelStepPer100;

  let zoom = 1;
  let tx = 0;
  let ty = 0;
  let drag = null; // { startX, startY, baseTx, baseTy } or null

  function clampPan(viewport) {
    const maxTx = (zoom - 1) * viewport.width / 2;
    const maxTy = (zoom - 1) * viewport.height / 2;
    if (tx >  maxTx) tx =  maxTx;
    if (tx < -maxTx) tx = -maxTx;
    if (ty >  maxTy) ty =  maxTy;
    if (ty < -maxTy) ty = -maxTy;
  }

  function getState() {
    return { zoom, tx, ty };
  }

  function onWheel({ deltaY, cursorX, cursorY, viewport }) {
    if (!viewport || !Number.isFinite(deltaY)) return;
    // factor < 1 zooms out, > 1 zooms in. deltaY negative = zoom in.
    const factor = Math.pow(wheelStepPer100, -deltaY / 100);
    const z0 = zoom;
    let z1 = z0 * factor;
    if (z1 < minZoom) z1 = minZoom;
    if (z1 > maxZoom) z1 = maxZoom;
    if (z1 === z0) return;

    // Focal-point math: cursor offset from viewport centre.
    const cx = (cursorX ?? viewport.width  / 2) - viewport.width  / 2;
    const cy = (cursorY ?? viewport.height / 2) - viewport.height / 2;
    const k = z1 / z0;
    tx = (tx - cx) * k + cx;
    ty = (ty - cy) * k + cy;
    zoom = z1;
    if (zoom <= minZoom + 1e-9) { tx = 0; ty = 0; }
    clampPan(viewport);
  }

  function onDragStart({ x, y, viewport }) {
    if (zoom <= minZoom + 1e-9 || !viewport) return;
    drag = { startX: x, startY: y, baseTx: tx, baseTy: ty };
  }

  function onDragMove({ x, y, viewport }) {
    if (drag === null || !viewport) return;
    tx = drag.baseTx + (x - drag.startX);
    ty = drag.baseTy + (y - drag.startY);
    clampPan(viewport);
  }

  /** End the gesture. Returns true if it started on a zoomed frame.
   *
   * viewer.js uses that to swallow the trailing click. There is no
   * click-vs-drag distance threshold on purpose: while zoomed the picture is
   * a pan surface, not a play/pause button, and a reviewer parked on a POI
   * clicks it far more often than they drag it.
   */
  function onDragEnd() {
    const startedPan = drag !== null;
    drag = null;
    return startedPan;
  }

  function reset() {
    zoom = 1; tx = 0; ty = 0; drag = null;
  }

  return { getState, onWheel, onDragStart, onDragMove, onDragEnd, reset };
}
